import { useEffect, useState } from "react";

import { api, type Vehicle } from "../api";
import { UNCLASSIFIED, classLabel, classesOf, inClass, sameClass, wireValue } from "../vehicleClass";
import { useAction, useLoad } from "../useLoad";
import { saidDifferently } from "../spokenName";
import { NameRecorder } from "./NameRecorder";
import { PhotoCapture } from "./PhotoCapture";
import { VehicleHistory } from "./VehicleHistory";

interface Props {
  teamId: string;
  revision: number;
  onError: (message: string) => void;
  /**
   * Requirement 7.15 — which class is on screen. Empty means "all", resolved inside.
   *
   * Lifted out of this component and into the URL. It used to be local `useState`, which meant it was
   * lost on every refresh *and* on every tab switch away and back — so a family filtered to the 1:24
   * trucks who glanced at the bracket came back to the whole garage.
   */
  showing: string;
  onShowing: (name: string) => void;
}

/** States where inference is still running, so the row is waiting on something. */
const WORKING = new Set(["pending", "processing"]);

/**
 * Task 8.4 — the garage. Add, photograph, rename, retire.
 *
 * **There is no delete, and there is no way to add one.** Requirement 7.9: past brackets, participation
 * records and the Hall of Fame reference vehicles permanently, so deleting one would corrupt history.
 * The API has no DELETE route on a vehicle at all — the absence is the enforcement — so the worst this
 * screen can do is retire, which removes a truck from selection while leaving every record intact.
 */
export function Garage({ teamId, revision, onError, showing, onShowing }: Props) {
  const { data, loading, reload, update } = useLoad(
    () => api.vehicles(teamId),
    [teamId, revision],
    onError,
  );
  const { isBusy, run } = useAction(onError);
  const [name, setName] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [capturing, setCapturing] = useState<Vehicle | null>(null);
  const [naming, setNaming] = useState<Vehicle | null>(null);
  const [history, setHistory] = useState<Vehicle | null>(null);

  /** Splices one vehicle's new server truth into the list, instead of re-reading the whole garage. */
  const replace = (vehicle: Vehicle) =>
    update((current) => ({
      ...current,
      vehicles: current.vehicles.map((existing) =>
        existing.vehicleId === vehicle.vehicleId ? vehicle : existing,
      ),
    }));

  const vehicles = data?.vehicles ?? [];
  const classes = classesOf(vehicles);
  // Falls back whenever the selection stops existing, which happens for real: reclassify the last truck
  // in a class, or retire it, and the filter this screen was showing is simply gone.
  const current = classes.some((name) => sameClass(name, showing)) ? showing : "";
  const all = vehicles.filter((vehicle) => !vehicle.isRetired);
  const active = current === "" ? all : inClass(all, current);
  const retired = vehicles.filter((vehicle) => vehicle.isRetired);
  // Deliberately every truck in the garage, not the filtered `active` list. This drives the poll
  // below, and computing it from the filtered list was a real bug: photograph three trucks, switch to
  // a class chip that does not contain them, and `working` was empty — so the poll never started and
  // the cutouts appeared only when the family gave up and reloaded the page. Whether a cutout is
  // running has nothing to do with which chip is selected.
  const working = all.filter((vehicle) => WORKING.has(vehicle.cutoutState ?? "none"));
  const withPhotos = vehicles.filter((vehicle) => vehicle.photoKey).length;

  // The cutout Lambda announces itself on the garage channel, and that bump is what normally refreshes
  // this screen within a second of inference finishing. This is the backstop for the case where the
  // socket is down: a phone that missed the event would otherwise sit on "Cutting out…" forever, and a
  // spinner that never resolves is worse than a slow one. Only runs while something is actually
  // pending, and stops on its own.
  //
  // Keyed on how many are working rather than on the list itself, so finishing one truck out of three
  // does not restart the other two's clock.
  useEffect(() => {
    if (working.length === 0) return;
    const timer = window.setInterval(reload, 4000);
    return () => window.clearInterval(timer);
  }, [working.length, reload]);

  const add = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // Added into the class on screen, which is the only sensible reading of adding a truck while
    // filtered to the 1:24 trucks. "All" adds it unclassified, where it is easy to find and sort.
    const assigned = current === "" ? undefined : wireValue(current) || undefined;
    setName("");
    void run(
      // `addVehicle` returns the created vehicle, so it can be appended rather than triggering a
      // re-read of the whole garage. The field is cleared before the request rather than after, so
      // typing the next name can start immediately.
      async () => {
        const created = await api.addVehicle(teamId, trimmed, assigned);
        update((currentData) => ({ ...currentData, vehicles: [...currentData.vehicles, created] }));
      },
      undefined,
      "add",
    );
  };

  return (
    <>
      <div className="card">
        <p className="section-title">Add a truck</p>
        <div className="inline-form">
          <input
            type="text"
            value={name}
            placeholder="Bone Shaker"
            maxLength={60}
            enterKeyHint="done"
            autoCapitalize="words"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && add()}
          />
          <button className="btn primary" disabled={isBusy("add") || !name.trim()} onClick={add}>
            Add
          </button>
        </div>
        {/* Requirement 2.9 — a name is the only requirement. A photo is an upgrade, never a gate. */}
        <p className="muted" style={{ marginTop: 8 }}>
          A name is all it needs. Add a photo whenever you like.
        </p>
      </div>

      {/*
        Requirement 7.15 — a garage of three kinds of toy is three garages sharing a screen. Absent when
        there is one class, because a filter with one option is a label pretending to be a control.
      */}
      {classes.length > 1 && (
        <div className="card">
          <p className="section-title">Class</p>
          <div className="chips">
            <button
              className={`chip${current === "" ? " on" : ""}`}
              onClick={() => onShowing("")}
            >
              All <span className="chip-count">{all.length}</span>
            </button>
            {classes.map((name) => (
              <button
                key={name}
                className={`chip${sameClass(name, current) ? " on" : ""}`}
                onClick={() => onShowing(name)}
              >
                {name} <span className="chip-count">{inClass(all, name).length}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <p className="section-title">
          {active.length} racing{loading ? " · refreshing" : ""}
        </p>
        {active.length === 0 && !loading && (
          <p className="muted">Nothing in the garage yet. Add a truck above.</p>
        )}
        <ul className="list">
          {active.map((vehicle) => (
            <VehicleRow
              key={vehicle.vehicleId}
              vehicle={vehicle}
              teamId={teamId}
              classes={classes}
              busy={isBusy(vehicle.vehicleId)}
              run={run}
              replace={replace}
              onCapture={() => setCapturing(vehicle)}
              onHistory={() => setHistory(vehicle)}
              onRecordName={() => setNaming(vehicle)}
            />
          ))}
        </ul>
      </div>

      {retired.length > 0 && (
        <div className="card">
          <div className="row">
            <p className="section-title grow" style={{ margin: 0 }}>
              {retired.length} retired
            </p>
            <button className="btn small" onClick={() => setShowRetired((value) => !value)}>
              {showRetired ? "Hide" : "Show"}
            </button>
          </div>
          {showRetired && (
            <ul className="list" style={{ marginTop: 8 }}>
              {retired.map((vehicle) => (
                <li key={vehicle.vehicleId} className="row retired">
                  <Thumbnail vehicle={vehicle} />
                  <span className="grow name">{vehicle.name ?? "—"}</span>
                  <button
                    className="btn small"
                    disabled={isBusy(vehicle.vehicleId)}
                    onClick={() =>
                      void run(
                        async () =>
                          replace(
                            await api.updateVehicle(teamId, vehicle.vehicleId, { isRetired: false }),
                          ),
                        undefined,
                        vehicle.vehicleId,
                      )
                    }
                  >
                    Un-retire
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/*
        Task 9.8, and the payoff for requirement 7.2 always keeping the original. Re-running the whole
        garage against a better model costs one inference call per truck and no re-shooting, so this is a
        button rather than a project. Hidden until there is something to reprocess.
      */}
      {withPhotos > 0 && (
        <div className="card">
          <p className="section-title">Cutouts</p>
          <p className="muted" style={{ marginBottom: 10 }}>
            Every original photo is kept, so all {withPhotos} can be cut out again — useful if a truck
            came out looking wrong.
          </p>
          <button
            className="btn wide"
            disabled={isBusy("reprocess")}
            onClick={() =>
              // The one mutation that really does touch every row, so re-reading the whole garage is
              // the right answer here rather than the lazy one. The backend now marks each vehicle
              // `processing` before returning, so the rows show progress immediately.
              void run(() => api.reprocessCutouts(teamId), reload, "reprocess")
            }
          >
            Redo all cutouts
          </button>
        </div>
      )}

      {history && (
        <VehicleHistory
          teamId={teamId}
          vehicle={history}
          onClose={() => setHistory(null)}
          onError={onError}
        />
      )}

      {naming && (
        <NameRecorder
          teamId={teamId}
          vehicle={naming}
          onClose={() => setNaming(null)}
          onSaved={replace}
          onError={onError}
        />
      )}

      {capturing && (
        <PhotoCapture
          teamId={teamId}
          vehicle={capturing}
          onClose={() => setCapturing(null)}
          onStarted={replace}
          onError={onError}
        />
      )}
    </>
  );
}

/**
 * Requirement 7.7 — **never a broken image.**
 *
 * Three cases in a deliberate order. A ready cutout wins. A rejected mask shows the framed original,
 * which requirement 7.6 treats as a success rather than a failure, so it is presented without any
 * warning decoration. Everything else — no photo, still processing, inference failed — gets the styled
 * placeholder, which is why `onError` on the `<img>` falls back to it too: a signed URL that has expired
 * mid-session must degrade to a designed card, not a grey icon.
 */
function Thumbnail({ vehicle }: { vehicle: Vehicle }) {
  const [broken, setBroken] = useState(false);
  const state = vehicle.cutoutState ?? "none";
  // Reset the moment a different URL arrives. `broken` latched for the life of the mount, which mattered
  // because signed URLs rotate and cutouts finish: a row whose expired URL failed once kept showing the
  // placeholder even after the server handed over a fresh, working one, and only a full page reload
  // cleared it. That is a large part of "if I manually refresh, all the photos show up".
  const source = vehicle.cutoutState === "ready" && vehicle.cutoutUrl
    ? vehicle.cutoutUrl
    : vehicle.usesFramedFallback && vehicle.photoUrl
      ? vehicle.photoUrl
      : null;
  useEffect(() => setBroken(false), [source]);

  if (source && !broken) {
    return (
      <span className={`thumb${vehicle.usesFramedFallback ? " framed" : ""}`}>
        <img src={source} alt="" loading="lazy" onError={() => setBroken(true)} />
      </span>
    );
  }

  return (
    <span className="thumb placeholder" aria-hidden="true">
      {WORKING.has(state) ? <span className="spinner small" /> : <span className="glyph">🚚</span>}
    </span>
  );
}

function VehicleRow({
  vehicle,
  teamId,
  classes,
  busy,
  run,
  replace,
  onCapture,
  onHistory,
  onRecordName,
}: {
  vehicle: Vehicle;
  teamId: string;
  /** Existing classes, offered as suggestions so a new one is deliberate rather than a typo. */
  classes: string[];
  busy: boolean;
  run: (work: () => Promise<unknown>, after?: () => void, key?: string) => void;
  /** Splices this vehicle's new server truth in, instead of re-reading the whole garage. */
  replace: (vehicle: Vehicle) => void;
  onCapture: () => void;
  onHistory: () => void;
  onRecordName: () => void;
}) {
  const [editing, setEditing] = useState<"name" | "class" | null>(null);
  const [draft, setDraft] = useState(vehicle.name ?? "");
  const [classDraft, setClassDraft] = useState(
    // Seeded with the real value rather than the label, so opening the editor on an unclassified truck
    // offers an empty field to fill instead of the word "Unclassified" to delete.
    vehicle.vehicleClass ?? "",
  );
  const state = vehicle.cutoutState ?? "none";

  if (editing === "class") {
    const listId = `classes-${vehicle.vehicleId}`;
    return (
      <li className="row">
        {/*
          Requirement 7.14 — pick an existing class or type a new one, in one control. A datalist is
          exactly that on a phone: the keyboard is there for a new class and the existing ones are one
          tap away, with no custom dropdown to build or to make accessible.
        */}
        <input
          type="text"
          list={listId}
          value={classDraft}
          placeholder="Class, or leave empty"
          maxLength={40}
          autoFocus
          enterKeyHint="done"
          autoCapitalize="words"
          onChange={(event) => setClassDraft(event.target.value)}
        />
        <datalist id={listId}>
          {classes
            .filter((name) => name !== UNCLASSIFIED)
            .map((name) => (
              <option key={name} value={name} />
            ))}
        </datalist>
        <button
          className="btn small primary"
          disabled={busy}
          onClick={() =>
            void run(
              async () => {
                const saved = await api.updateVehicle(teamId, vehicle.vehicleId, {
                  // Empty clears it back to unclassified. Requirement 7.14 makes that a real state, so
                  // it has to be reachable — a class set by mistake must not be permanent.
                  vehicleClass: wireValue(classDraft),
                });
                // The response *is* the new truth for this truck, so it goes straight into the list.
                // This used to close the editor and re-read the entire garage, which meant the row you
                // just edited took as long to update as the garage took to list — and listing got
                // slower with every truck the family added.
                setEditing(null);
                replace(saved);
              },
              undefined,
              vehicle.vehicleId,
            )
          }
        >
          Save
        </button>
        <button className="btn small" onClick={() => setEditing(null)}>
          Cancel
        </button>
      </li>
    );
  }

  if (editing === "name") {
    return (
      <li className="row">
        <input
          type="text"
          value={draft}
          maxLength={60}
          autoFocus
          enterKeyHint="done"
          onChange={(event) => setDraft(event.target.value)}
        />
        <button
          className="btn small primary"
          disabled={busy || !draft.trim()}
          onClick={() => {
            // A recording says the *old* name, so a real rename makes it wrong rather than merely stale and
            // the server discards it. That is the right behaviour and a bad surprise, so it is confirmed
            // here — the one place somebody can still change their mind.
            //
            // Only when it would actually cost something: a recording exists, and the new name would be
            // read aloud differently. Correcting capitalisation keeps the take, so prompting for it would
            // be a warning about nothing, which is how people learn to dismiss warnings.
            if (vehicle.recordedNameKey && saidDifferently(vehicle.name ?? "", draft)) {
              const ok = window.confirm(
                `You recorded your own voice saying “${vehicle.name}”.\n\n` +
                  `Renaming to “${draft.trim()}” will delete that recording and replace it with the ` +
                  `computer voice. You can record the new name afterwards.`,
              );
              if (!ok) return;
            }
            void run(
              async () => {
                const saved = await api.updateVehicle(teamId, vehicle.vehicleId, {
                  name: draft.trim(),
                });
                setEditing(null);
                replace(saved);
              },
              undefined,
              vehicle.vehicleId,
            );
          }}
        >
          Save
        </button>
        <button className="btn small" onClick={() => setEditing(null)}>
          Cancel
        </button>
      </li>
    );
  }

  return (
    <li className="vehicle">
      <div className="row">
        <Thumbnail vehicle={vehicle} />
        <span className="grow">
          <span className="name">{vehicle.name ?? "—"}</span>
          <div className="muted">{classLabel(vehicle)}</div>
          <CutoutNote vehicle={vehicle} />
        </span>
        <button className="btn small" onClick={onCapture}>
          {state === "none" ? "Add photo" : "Retake"}
        </button>
      </div>
      <div className="row subrow">
        <span className="grow" />
        {/* Requirement 8.3 — every race this truck has been in, across all tournaments. */}
        <button className="btn small" onClick={onHistory}>
          Races
        </button>
        <button className="btn small" onClick={() => setEditing("name")}>
          Rename
        </button>
        {/* Requirement 7.13 — the class is set here, on the phone, per 7.13's "set on the phone". */}
        <button className="btn small" onClick={() => setEditing("class")}>
          Class
        </button>
        {/*
          The announcer's spoken name. Three states rather than two, because a synthesised stand-in is not
          the same as a recording and somebody working through eighty-four cars needs to see at a glance
          which ones are still a computer voice.
        */}
        <button className="btn small" onClick={onRecordName}>
          {vehicle.nameVoice === "recorded"
            ? "Voice ✓"
            : vehicle.nameVoice === "auto"
              ? "Auto voice"
              : "Add voice"}
        </button>
        <button
          className="btn small danger"
          disabled={busy}
          onClick={() =>
            void run(
              async () =>
                replace(await api.updateVehicle(teamId, vehicle.vehicleId, { isRetired: true })),
              undefined,
              vehicle.vehicleId,
            )
          }
        >
          Retire
        </button>
      </div>
    </li>
  );
}

/**
 * One line under the name, and only when it earns its place.
 *
 * A ready cutout says nothing — the picture is the feedback. A fallback says what to change, because
 * `cutoutReason` is the only place the coaching of requirement 7.1 gets to be specific: "the background
 * was too busy" tells someone to move the truck onto a plain surface, which is exactly the fix.
 */
function CutoutNote({ vehicle }: { vehicle: Vehicle }) {
  const state = vehicle.cutoutState ?? "none";
  if (state === "ready" || state === "none") return null;

  if (WORKING.has(state)) {
    return <span className="note working">Cutting out…</span>;
  }
  if (state === "fallback") {
    return (
      <span className="note">
        Using the photo as-is{vehicle.cutoutReason ? ` — ${vehicle.cutoutReason}` : ""}. Retake on a
        plainer surface for a cutout.
      </span>
    );
  }
  // The reason when there is one, and only *then* the suggestion to retake.
  //
  // This used to say "Retaking usually fixes it" unconditionally, which for the one failure that
  // actually happened — the photo being larger than the cutout service accepts — was advice that could
  // not work: the same camera produces the same pixel count. Somebody followed it twice.
  return (
    <span className="note warn">
      {vehicle.cutoutReason
        ? `Cutout didn't work — ${vehicle.cutoutReason}.`
        : "Cutout didn't work. Retaking usually fixes it."}
    </span>
  );
}
